import React from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { RelativeContainer } from './Card';
import { Grid } from './Grid';
import { List } from './List';
import Chip from './Chip';
import Label from './Label';
import Headline2 from './Typography/Heading/Headline2';
import Body2 from './Typography/Body/Body2';
import SubTitle from './Typography/SubTitle';
import ArticleBody from './InnerHtmlStyling';
import Labels from './Card/Labels';
import breakpoints from './Theme/breakpoints';

export const PartnersArticleContainer = styled.div`
  && {
    a {
      color: var(--black-high);
    }
  }
`;

const ArticleHeadline = styled(Headline2)`
  && {
    margin-bottom: 8px;

    @media (min-width: ${breakpoints.md}px) {
      margin-bottom: 20px;
    }
  }
`;

export const ArticleGrid = styled(Grid)`
  &&& {
    max-width: 1100px;
    padding: 0 8px 16px;

    @media (min-width: ${breakpoints.md}px) {
      padding: 0 12px;
    }
  }
`;

const ArticleContainer = styled.div`
  display: block;
  background-color: #fff;

  img {
    width: 100%;
  }
  figure {
    margin: 0;
  }
  && figcaption {
    color: var(--grey-dark);
    font-family: var(--font-article);
    padding: 0 1px;
    text-align: left;
    line-height: 1.25rem;
    font-size: 12px;
  }
  .image-container {
    margin-bottom: 16px;
  }
  .image-container + figcaption {
    margin: -16px 0 16px 8px;
  }
`;

const HeaderImageContainer = styled(RelativeContainer)`
  background: rgb(255,255,255);
  background: linear-gradient(180deg,
  rgba(255,255,255,1) 0%, rgba(187,187,187,1) 68%);

  padding-bottom: calc(66.67% + 18px);
  width: calc(100% + 32px);
  left: -16px;
  @media (min-width: ${breakpoints.sm}px) {
    padding-bottom: 66.67%;
    left: auto;
    width: auto;
  }
`;

const TextContainer = styled.div`
  padding: 8px 0 20px;
  max-width: 550px;
  margin: 24px auto 0;

  @media (min-width: ${breakpoints.md}px) {
    padding: 12px 24px 24px;
    margin: 0 auto;
  }
`;

const CreatedBy = styled(SubTitle)`
  && a {
    color: var(--grey-dark);
  }
`;

const ArticleFlexSubContainer = styled.div`

  @media (min-width: ${breakpoints.xssm}px) {
    display: flex;
    margin-bottom: 8px;
    justify-content: space-between;
  }

  @media (min-width: ${breakpoints.md}px) {
    margin-bottom: 0;
  }
`;

const ArticleHeader = styled.header`
  display: flex;
  flex-direction: column;
`;

interface ArticleHeaderTextProperties { fullHeightTitle: boolean }
const ArticleHeaderText = styled.div<ArticleHeaderTextProperties>`
  display: flex;
  flex-direction: column;
  ${(properties) => properties.fullHeightTitle && `
    min-height: calc(50vh - 100px);
    word-break: break-word;
    justify-content: flex-end;
  `}

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
    justify-content: flex-start;
  }
`;

interface ArticleSubContainerProperties { fullHeightTitle: boolean }
const ArticleSubContainer = styled.div<ArticleSubContainerProperties>`
  margin: 0 0 12px;

  ${(properties) => properties.fullHeightTitle && `
    min-height: calc(50vh - 48px);
    word-break: break-word;
  `}

  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }

  @media (min-width: ${breakpoints.md}px) {
    order: 1;
    min-width: 550px;
    margin: 36px auto 0;
  }
`;

const ArticleLabel = styled(SubTitle)`
  && {
    margin: 0;
    color: var(--black-high);
  }
`;

export const ImageContainer = styled.div`
  width: 102px;
  padding-right: 16px;
`;

export const StyledList = styled(List)`
  && {
    background-color: var(--white-light);
  }
`;

export const StyledBody2 = styled(Body2)`
  && {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
  }
`;

export const TagChip = styled(Chip)`
  && {
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-weight: 500;
    font-size: .75rem;
  }
`;

const SocialButtons = styled.div`
  margin: -6px 0 6px -12px;
  height: 40px;

  @media(min-width: ${breakpoints.xssm}px) {
    margin-left: 0;
    height: 60px;
    width: 200px;
    overflow: visible;
  }
`;

const Date = styled(SubTitle)`
  && {
    margin: 0;
  }
`;

interface DynamicShareButtonContainerProperties { center: boolean }
export const DynamicShareButtonContainer = styled.div<
DynamicShareButtonContainerProperties
>`
  text-align: ${(properties) => properties.center && 'center'};
  height: 40px;
  margin: 32px 0;
`;

const articleSubHeaderPropertyTypes = {
  createdAt: PropTypes.element,
  articleBy: PropTypes.node,
  children: PropTypes.node,
  socialButtons: PropTypes.node,
  sponsorMessage: PropTypes.string,
  pressReleaseMessage: PropTypes.string,
  fullHeightTitle: PropTypes.bool,
};

type ArticleSubHeaderProperties = InferProps<
  typeof articleSubHeaderPropertyTypes
>;

export const ArticleSubHeader = ({
  articleBy,
  createdAt,
  children,
  socialButtons,
  pressReleaseMessage,
  sponsorMessage,
  fullHeightTitle,
} : ArticleSubHeaderProperties) => (
  <ArticleSubContainer fullHeightTitle={fullHeightTitle ?? true}>
    {sponsorMessage && (
      <ArticleLabel tag="span">{sponsorMessage}</ArticleLabel>
    )}
    {pressReleaseMessage && (
      <ArticleLabel tag="span">{pressReleaseMessage}</ArticleLabel>
    )}
    <ArticleFlexSubContainer>
      <CreatedBy tag="div">
        {articleBy && articleBy}
        <Date tag="p" variant="subtitle2">
          {createdAt}
        </Date>
      </CreatedBy>
      {children}
      <SocialButtons>{socialButtons}</SocialButtons>
    </ArticleFlexSubContainer>
  </ArticleSubContainer>
);

ArticleSubHeader.propTypes = articleSubHeaderPropertyTypes;

const articlePropertyTypes = {
  title: PropTypes.string,
  category: PropTypes.shape({
    title: PropTypes.string,
  }),
  children: PropTypes.node,
  translatedCategoryTitle: PropTypes.string,
  articleBy: PropTypes.node,
  createdAt: PropTypes.element,
  body: PropTypes.string,
  image: PropTypes.node,
  imageCaption: PropTypes.string,
  socialButtons: PropTypes.node,
  labels: PropTypes.arrayOf(PropTypes.string),
  sponsorMessage: PropTypes.string,
  pressReleaseMessage: PropTypes.string,
  fullHeightTitle: PropTypes.bool,
};

type ArticleProperties = InferProps<
  typeof articlePropertyTypes
>;

const Article = ({
  category,
  translatedCategoryTitle,
  title,
  articleBy,
  createdAt,
  body,
  image,
  imageCaption,
  children,
  socialButtons,
  labels,
  sponsorMessage,
  pressReleaseMessage,
  fullHeightTitle,
} : ArticleProperties) => (
  <ArticleContainer>
    <ArticleHeader>
      <ArticleHeaderText fullHeightTitle={fullHeightTitle ?? true}>
        <ArticleHeadline tag="h1">
          {title}
        </ArticleHeadline>
      </ArticleHeaderText>
      <ArticleSubHeader
        fullHeightTitle={fullHeightTitle}
        articleBy={articleBy}
        createdAt={createdAt}
        socialButtons={socialButtons}
        sponsorMessage={sponsorMessage}
        pressReleaseMessage={pressReleaseMessage}
      />
      {image && (
        <figure>
          <HeaderImageContainer>
            {image}
            <Label
              className="highlight"
            >
              {translatedCategoryTitle ?? category?.title}
              <Labels labels={labels} />
            </Label>
          </HeaderImageContainer>
          {imageCaption && <figcaption>{imageCaption}</figcaption>}
        </figure>
      )}
    </ArticleHeader>
    <TextContainer>
      <ArticleBody
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </TextContainer>
    {children}
  </ArticleContainer>
);

Article.propTypes = articlePropertyTypes;

export default Article;
